import { useState } from "react";
import { ExternalLink, Users, Edit3, LinkIcon, CheckCircle2, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface ClaimProfileModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  handle: string;
  profileXUserId: string;
  initialVerifiedXUserId?: string;
}

const BENEFITS = [
  {
    icon: LinkIcon,
    title: "Drive traffic to your X profile",
    description: "Your handle becomes a clickable link straight to your X.",
  },
  {
    icon: ExternalLink,
    title: "Drive traffic to your posts",
    description: "Every call links directly to the original tweet.",
  },
  {
    icon: Users,
    title: "Grow your following",
    description: "A follow button on your profile — no one has to leave the platform.",
  },
  {
    icon: Edit3,
    title: "Correct misinterpreted calls",
    description: "Fix wrong classifications and improve your Win Rate & Avg PnL.",
  },
];

const XIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export default function ClaimProfileModal({
  open,
  onOpenChange,
  handle,
  profileXUserId,
  initialVerifiedXUserId,
}: ClaimProfileModalProps) {
  const [verifying, setVerifying] = useState(false);
  const [verifiedXUserId, setVerifiedXUserId] = useState<string | null>(initialVerifiedXUserId ?? null);
  const [checkingOut, setCheckingOut] = useState(false);

  const isOwnerVerified = verifiedXUserId === profileXUserId;

  const handleConnectX = async () => {
    setVerifying(true);
    try {
      const redirectUri = `${window.location.origin}/auth/callback`;
      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/x-auth-start`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({ handle: handle.replace(/^@/, ""), redirect_uri: redirectUri }),
        }
      );
      if (!resp.ok) throw new Error("Failed to start X auth");
      const { url } = await resp.json();
      window.location.href = url;
    } catch (err) {
      console.error("X auth error:", err);
      setVerifying(false);
    }
  };

  const handleClaim = async () => {
    setCheckingOut(true);
    try {
      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-claim-checkout`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({
            handle: handle.replace(/^@/, ""),
            x_user_id: verifiedXUserId,
          }),
        }
      );
      if (!resp.ok) throw new Error("Failed to create checkout");
      const { url } = await resp.json();
      window.location.href = url;
    } catch (err) {
      console.error("Checkout error:", err);
      setCheckingOut(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-card border-border">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-foreground">
            Claim @{handle.replace(/^@/, "")}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Take ownership of this profile and unlock premium features.
          </DialogDescription>
        </DialogHeader>

        {/* Benefits */}
        <div className="space-y-3 py-2">
          {BENEFITS.map((b) => (
            <div key={b.title} className="flex items-start gap-3">
              <div className="mt-0.5 p-1.5 rounded-md bg-primary/10">
                <b.icon className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">{b.title}</p>
                <p className="text-xs text-muted-foreground">{b.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Price */}
        <div className="text-center py-3 border-y border-border">
          <span className="text-2xl font-bold text-foreground">$19.99</span>
          <span className="text-muted-foreground text-sm"> / month</span>
        </div>

        {/* Step 1: Connect with X */}
        <div className="space-y-3 pt-1">
          {isOwnerVerified ? (
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-profit/10 border border-profit/30">
              <CheckCircle2 className="w-4 h-4 text-profit shrink-0" />
              <span className="font-mono text-sm text-profit">
                Verified as @{handle.replace(/^@/, "")}
              </span>
            </div>
          ) : (
            <Button
              onClick={handleConnectX}
              disabled={verifying}
              className="w-full gap-2 bg-foreground text-background hover:bg-foreground/90"
            >
              {verifying ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <XIcon />
              )}
              Connect with X to verify ownership
            </Button>
          )}

          {/* Step 2: Claim */}
          <Button
            onClick={handleClaim}
            disabled={!isOwnerVerified || checkingOut}
            className="w-full gap-2"
          >
            {checkingOut ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : null}
            Claim your profile now
          </Button>

          {!isOwnerVerified && verifiedXUserId && (
            <p className="text-xs text-loss text-center">
              The X account you connected doesn't match this profile.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
